import logger from '../../log';
import sendEdgeCacheFailureDiagnostic from './sendEdgeCacheFailureDiagnostic';
import { ProxyModifier } from './Modifier';
import {
  ObjectRequest,
  ObjectRequestMapper,
  ObjectResponse
} from '../interfaces';
import { ProxyConfig } from '../../config/ProxyConfig';
import { PlayType } from '../../common/constants';
import { SourceType } from '../../webrtc/agent/interfaces';
import getAdditionalHeaders from './getAdditionalHeaders';
import getRequestMapper from './EdgeCacheRequestMapper';
import getTimeoutSignal from '../../util/getTimeoutSignal';
import { ApiInfo, Operation } from './interfaces';

const log = logger.getLogger(__filename);

interface CheckArgs {
  baseUrl: string;
  timeout: number;
  operation?: Operation;
}

const checkHealthZ = async ({
  baseUrl,
  timeout,
  operation
}: CheckArgs): Promise<boolean> => {
  const path =
    operation && operation.name == 'getHealth' ? operation.uri : '/healthz';
  const url = new URL(path, baseUrl).toString();
  try {
    // timeout the status check with the proxy configs timeout value.
    const resp = await fetch(url, { signal: getTimeoutSignal(timeout) });
    log.debug('EC healthz: ', resp);
    return true;
  } catch (error) {
    // if this call fails, proxy config is down.
    sendEdgeCacheFailureDiagnostic({ url, error, from: 'checkHealthZ' });
    return false;
  }
};

const checkApiInfo = async ({
  baseUrl,
  timeout
}: CheckArgs): Promise<ApiInfo | undefined> => {
  const url = new URL('/api/info', baseUrl).toString();
  try {
    // timeout the status check with the proxy configs timeout value.
    const resp = await fetch(url, { signal: getTimeoutSignal(timeout) });
    const js = await resp.json();
    log.debug('EC api info: ', resp, js);
    return js;
  } catch (error) {
    // if this call fails, proxy config is down.
    sendEdgeCacheFailureDiagnostic({ url, error, from: 'checkApiInfo' });
    return undefined;
  }
};

export default class EdgeCacheModifier
  implements ProxyModifier<ObjectRequest, ObjectResponse>
{
  app: string;
  proxyConfig: ProxyConfig;
  sdkVer: string;
  sessionToken: string;
  playType: PlayType;
  initPromise: Promise<ObjectRequestMapper>;
  tenantId: string;
  contentId: string;

  constructor({
    playType,
    proxyConfig,
    app,
    sdkVer,
    sessionToken,
    tenantId,
    contentId
  }: {
    proxyConfig: ProxyConfig;
    app: string;
    sdkVer: string;
    sessionToken: string;
    playType: PlayType;
    tenantId: string;
    contentId: string;
  }) {
    this.app = app;
    this.proxyConfig = proxyConfig;
    this.sdkVer = sdkVer;
    this.playType = playType;
    this.sessionToken = sessionToken;
    this.tenantId = tenantId;
    this.contentId = contentId;
    const modifiedBaseUrl = `${this.proxyConfig.scheme}://${this.proxyConfig.host}`;

    const args = {
      baseUrl: modifiedBaseUrl,
      timeout: this.proxyConfig.timeout * 1000
    };
    this.initPromise = (async (
      args: CheckArgs
    ): Promise<ObjectRequestMapper> => {
      const apiInfo = await checkApiInfo(args);

      const ops = apiInfo ? apiInfo.operations : [];
      const res = await checkHealthZ({
        ...args,
        operation: ops.find(o => o.name == 'getHealth')
      });

      if (!res) {
        proxyConfig.disabled = true;
      }
      return getRequestMapper({
        edgeCacheBaseUrl: modifiedBaseUrl,
        playType,
        cacheDurations: proxyConfig.cacheDurations,
        timeout: proxyConfig.timeout * 1000,
        app,
        sdkVer,
        sessionToken,
        additionalHeaders: getAdditionalHeaders(proxyConfig, apiInfo?.version),
        apiInfo,
        tenantId,
        contentId
      });
    })(args);
  }

  getTransferSourceType(response: ObjectResponse): SourceType {
    const { headers } = response;
    if (!headers) {
      return SourceType.PROXY;
    }
    const header = Array.from(headers.keys()).find(v => {
      const header = v && v.toLowerCase();
      return header && header === 'x-kollective-proxy-cache';
    });
    const cache = header && headers.get(header);
    if (cache && cache.toLowerCase() == 'hit') {
      return SourceType.PROXY_CACHE;
    } else {
      return SourceType.PROXY;
    }
  }

  initialize(): Promise<ObjectRequestMapper> {
    return this.initPromise;
  }
}
